import { useCallback } from 'react';
import { apiKeysClient, CreateApiKeyModel, CreateScopedSessionModel } from '@bymason/controller-api';
import { useDeleteDataHook, useGetDataHook } from '@bymason/api-hooks-base';

export { ApiKeysModel, CreateApiKeyResponse, CreateScopedSessionModel } from '@bymason/controller-api';

/**
 * React hook that asynchronously gets an ApiKeys.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGetApiKeys(clientToken: string) {
  const handleGetData = useCallback(() => {
    return apiKeysClient.getApiKeys(clientToken);
  }, [clientToken]);

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously gets api key scopes.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGetApiScopes(clientToken: string) {
  const handleGetData = useCallback(() => {
    return apiKeysClient.getApiKeyScopes(clientToken);
  }, [clientToken]);

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously deletes an Api key by id
 * @returns HookArray
 * @HookArray [0] delete method
 * @HookArray [1] error
 * @HookArray [2] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 */
export function useDeleteApiKey(clientToken: string) {
  const handleGetData = useCallback(
    (id: string) => {
      return apiKeysClient.deleteApiKey(id, clientToken);
    },
    [clientToken]
  );
  return useDeleteDataHook(handleGetData);
}

/**
 * React hook that asynchronously creates Api Key.
 * @returns HookArray
 * @HookArray [0] post method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useCreateApiKey(clientToken: string) {
  const handleGetData = useCallback(
    (options: CreateApiKeyModel) => {
      return apiKeysClient.createApiKey(options, clientToken);
    },
    [clientToken]
  );
  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously creates a scoped session.
 * @returns HookArray
 * @HookArray [0] post method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useCreateScopedSession(clientToken: string) {
  const handleGetData = useCallback(
    (options: CreateScopedSessionModel) => {
      return apiKeysClient.createScopedSession(options, clientToken);
    },
    [clientToken]
  );
  return useGetDataHook(handleGetData);
}
